import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  Calculator, DollarSign, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, Edit, Save, Loader2, Building, Calendar, FileText,
  PiggyBank, Percent, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = '€') => {
  if (!amount && amount !== 0) return '—';
  return `${currency}${amount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatPercent = (value) => {
  if (!value && value !== 0) return '—';
  return `${value.toFixed(1)}%`;
};

const getProfitabilityBadge = (status, label) => {
  switch (status) {
    case 'healthy':
      return (
        <Badge className="bg-green-500 text-white">
          <CheckCircle className="h-3 w-3 mr-1" />
          {label || 'Rentable'}
        </Badge>
      );
    case 'warning':
      return (
        <Badge className="bg-orange-500 text-white">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {label || 'À surveiller'}
        </Badge>
      );
    case 'danger':
      return (
        <Badge className="bg-red-500 text-white">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {label || 'Non rentable'}
        </Badge>
      );
    default:
      return (
        <Badge variant="outline">
          <Minus className="h-3 w-3 mr-1" />
          Non calculé
        </Badge>
      );
  }
};

const CandidateFinancialsCard = ({ candidateId, candidateName }) => {
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [editingEstimation, setEditingEstimation] = useState(false);
  const [editingMission, setEditingMission] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [estimationForm, setEstimationForm] = useState({
    salary_gross_monthly: 0,
    salary_net_monthly: 0,
    pricing_type: 'assistant'
  });
  
  const [missionForm, setMissionForm] = useState({
    is_on_mission: false,
    client_name: '',
    billed_amount_monthly: 0,
    salary_gross_monthly: 0,
    salary_net_monthly: 0,
    mission_start_date: '',
    notes: ''
  });

  useEffect(() => {
    if (candidateId) {
      fetchFinancials();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const fetchFinancials = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/candidates/${candidateId}/financials`);
      if (response.ok) {
        const data = await response.json();
        setFinancials(data);
        setHasAccess(true);
        
        // Populate forms
        if (data.estimation) {
          setEstimationForm({
            salary_gross_monthly: data.estimation.salary_gross_monthly || 0,
            salary_net_monthly: data.estimation.salary_net_monthly || 0,
            pricing_type: data.estimation.pricing_type || 'assistant'
          });
        }
        if (data.mission) {
          setMissionForm({
            is_on_mission: true,
            client_name: data.mission.client_name || '',
            billed_amount_monthly: data.mission.billed_amount_monthly || 0,
            salary_gross_monthly: data.mission.salary_gross_monthly || 0,
            salary_net_monthly: data.mission.salary_net_monthly || 0,
            mission_start_date: data.mission.mission_start_date || '',
            notes: data.mission.notes || ''
          });
        }
      } else if (response.status === 403) {
        setHasAccess(false);
      }
    } catch (error) {
      console.error('Erreur chargement financials:', error);
      if (error.message?.includes('403')) {
        setHasAccess(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEstimation = async () => {
    setSaving(true);
    try {
      const response = await api.put(
        `/admin/candidates/${candidateId}/financials/estimation`,
        estimationForm
      );
      if (response.ok) {
        toast.success('Estimation mise à jour');
        setEditingEstimation(false);
        fetchFinancials();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMission = async () => {
    setSaving(true);
    try {
      const response = await api.put(
        `/admin/candidates/${candidateId}/financials/mission`,
        missionForm
      );
      if (response.ok) {
        toast.success('Mission mise à jour');
        setEditingMission(false);
        fetchFinancials();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 border-tp-border">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-tp-text-light" />
          <span className="ml-2 text-tp-text-light">Chargement des données financières...</span>
        </div>
      </Card>
    );
  }

  if (!hasAccess) {
    return (
      <Card className="p-6 border-tp-border bg-tp-bg/50">
        <div className="text-center py-4">
          <AlertTriangle className="h-8 w-8 text-tp-warning mx-auto mb-2" />
          <p className="text-tp-text-light text-sm">
            Données financières réservées aux super administrateurs
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="candidate-financials-card">
      {/* Indicateur de rentabilité global */}
      {financials?.profitability && (
        <div className="flex items-center justify-between p-4 bg-tp-bg rounded-lg">
          <div className="flex items-center gap-3">
            <PiggyBank className="h-6 w-6 text-tp-primary" />
            <div>
              <p className="text-sm font-medium text-tp-text">Statut rentabilité</p>
              <p className="text-xs text-tp-text-light">Marge réelle: {formatPercent(financials.profitability.margin_percent)}</p>
            </div>
          </div>
          {getProfitabilityBadge(financials.profitability.status, financials.profitability.label)}
        </div>
      )}

      {/* Bloc Estimation Interne */}
      <Card className="p-6 border-tp-border" data-testid="estimation-block">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-tp-text flex items-center gap-2">
            <Calculator className="h-5 w-5 text-tp-accent" />
            Estimation interne
          </h3>
          <Button variant="outline" size="sm" onClick={() => setEditingEstimation(true)}>
            <Edit className="h-4 w-4 mr-1" />
            Modifier
          </Button>
        </div>

        {!financials?.has_financial_data ? (
          <p className="text-tp-text-light text-sm">
            Aucune donnée financière. Cliquez sur "Modifier" pour configurer.
          </p>
        ) : financials?.estimation && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-tp-text-light">Type profil</p>
                <p className="font-medium text-tp-text capitalize">{financials.estimation.pricing_type}</p>
              </div>
              <div>
                <p className="text-xs text-tp-text-light">Salaire brut</p>
                <p className="font-medium text-tp-text">{formatCurrency(financials.estimation.salary_gross_monthly)}</p>
              </div>
              <div>
                <p className="text-xs text-tp-text-light">Coût total</p>
                <p className="font-medium text-tp-text">{formatCurrency(financials.estimation.estimated_total_cost_monthly)}</p>
              </div>
              <div>
                <p className="text-xs text-tp-text-light">Marge cible</p>
                <p className="font-medium text-tp-text">{formatPercent(financials.estimation.target_margin_percent)}</p>
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between p-4 bg-tp-primary/5 rounded-lg">
              <div>
                <p className="text-sm text-tp-text-light">Prix de vente recommandé</p>
                <p className="text-2xl font-bold text-tp-primary">
                  {formatCurrency(financials.estimation.recommended_sale_price_monthly)}
                  <span className="text-sm font-normal text-tp-text-light"> /mois</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-tp-text-light">Marge estimée</p>
                <p className="text-lg font-semibold text-green-600">
                  {formatCurrency(financials.estimation.estimated_margin_amount)}
                  <span className="text-sm ml-1">({formatPercent(financials.estimation.estimated_margin_percent)})</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Bloc Mission Active */}
      <Card className="p-6 border-tp-border" data-testid="mission-block">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-tp-text flex items-center gap-2">
            <Building className="h-5 w-5 text-tp-accent" />
            Mission active
          </h3>
          <Button variant="outline" size="sm" onClick={() => setEditingMission(true)}>
            <Edit className="h-4 w-4 mr-1" />
            {financials?.mission ? 'Modifier' : 'Ajouter mission'}
          </Button>
        </div>

        {!financials?.mission ? (
          <p className="text-tp-text-light text-sm">
            Pas de mission en cours. Cliquez sur "Ajouter mission" pour affecter ce candidat.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-tp-text-light">Client</p>
                <p className="font-semibold text-tp-text text-lg">{financials.mission.client_name || '—'}</p>
              </div>
              {getProfitabilityBadge(financials.mission.profitability_status, financials.mission.profitability_label)}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-tp-text-light">Facturé client</p>
                <p className="font-medium text-tp-text">{formatCurrency(financials.mission.billed_amount_monthly)}/mois</p>
              </div>
              <div>
                <p className="text-xs text-tp-text-light">Salaire brut</p>
                <p className="font-medium text-tp-text">{formatCurrency(financials.mission.salary_gross_monthly)}</p>
              </div>
              <div>
                <p className="text-xs text-tp-text-light">Salaire net</p>
                <p className="font-medium text-tp-text">{formatCurrency(financials.mission.salary_net_monthly)}</p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-tp-text-light">Coût total réel</p>
                <p className="font-medium text-red-600">{formatCurrency(financials.mission.real_total_cost_monthly)}</p>
              </div>
              <div>
                <p className="text-xs text-tp-text-light">Marge réelle</p>
                <p className={`font-semibold ${
                  financials.mission.real_margin_amount >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(financials.mission.real_margin_amount)}
                </p>
              </div>
              <div>
                <p className="text-xs text-tp-text-light">% Marge</p>
                <p className={`font-semibold ${
                  financials.mission.real_margin_percent >= 20 ? 'text-green-600' :
                  financials.mission.real_margin_percent >= 10 ? 'text-orange-600' : 'text-red-600'
                }`}>
                  {formatPercent(financials.mission.real_margin_percent)}
                </p>
              </div>
            </div>

            {/* Comparaison Estimation vs Réel */}
            {financials?.comparison && (
              <div className={`p-4 rounded-lg border ${
                financials.comparison.interpretation === 'below_recommended' 
                  ? 'bg-red-50 border-red-200' 
                  : financials.comparison.interpretation === 'above_recommended'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-tp-bg border-tp-border'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-tp-text">Écart prix recommandé</p>
                    <p className="text-xs text-tp-text-light">
                      Recommandé: {formatCurrency(financials.estimation?.recommended_sale_price_monthly)} 
                      {' → '}
                      Facturé: {formatCurrency(financials.mission.billed_amount_monthly)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {financials.comparison.pricing_gap < 0 ? (
                      <ArrowDownRight className="h-5 w-5 text-red-500" />
                    ) : financials.comparison.pricing_gap > 0 ? (
                      <ArrowUpRight className="h-5 w-5 text-green-500" />
                    ) : (
                      <Minus className="h-5 w-5 text-tp-text-light" />
                    )}
                    <span className={`font-semibold ${
                      financials.comparison.pricing_gap < 0 ? 'text-red-600' : 
                      financials.comparison.pricing_gap > 0 ? 'text-green-600' : 'text-tp-text'
                    }`}>
                      {financials.comparison.pricing_gap > 0 ? '+' : ''}
                      {formatCurrency(financials.comparison.pricing_gap)}
                      {' '}({financials.comparison.pricing_gap > 0 ? '+' : ''}{formatPercent(financials.comparison.pricing_gap_percent)})
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Dialog Estimation */}
      <Dialog open={editingEstimation} onOpenChange={setEditingEstimation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurer l'estimation</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Type de profil</Label>
              <Select 
                value={estimationForm.pricing_type} 
                onValueChange={(v) => setEstimationForm(prev => ({...prev, pricing_type: v}))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assistant">Assistant Virtuel</SelectItem>
                  <SelectItem value="tech">Talent Technique</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Salaire brut mensuel (€)</Label>
              <Input
                type="number"
                value={estimationForm.salary_gross_monthly || ''}
                onChange={(e) => setEstimationForm(prev => ({
                  ...prev, 
                  salary_gross_monthly: parseFloat(e.target.value) || 0
                }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Salaire net mensuel (€) - optionnel</Label>
              <Input
                type="number"
                value={estimationForm.salary_net_monthly || ''}
                onChange={(e) => setEstimationForm(prev => ({
                  ...prev, 
                  salary_net_monthly: parseFloat(e.target.value) || 0
                }))}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEstimation(false)}>Annuler</Button>
            <Button onClick={handleSaveEstimation} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Mission */}
      <Dialog open={editingMission} onOpenChange={setEditingMission}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configurer la mission</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_on_mission"
                checked={missionForm.is_on_mission}
                onChange={(e) => setMissionForm(prev => ({...prev, is_on_mission: e.target.checked}))}
                className="h-4 w-4"
              />
              <Label htmlFor="is_on_mission">Candidat en mission active</Label>
            </div>

            {missionForm.is_on_mission && (
              <>
                <div>
                  <Label>Nom du client</Label>
                  <Input
                    value={missionForm.client_name}
                    onChange={(e) => setMissionForm(prev => ({...prev, client_name: e.target.value}))}
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Prix facturé client (€/mois)</Label>
                    <Input
                      type="number"
                      value={missionForm.billed_amount_monthly || ''}
                      onChange={(e) => setMissionForm(prev => ({
                        ...prev, 
                        billed_amount_monthly: parseFloat(e.target.value) || 0
                      }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Date de début</Label>
                    <Input
                      type="date"
                      value={missionForm.mission_start_date || ''}
                      onChange={(e) => setMissionForm(prev => ({...prev, mission_start_date: e.target.value}))}
                      className="mt-1"
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Salaire brut réel (€/mois)</Label>
                    <Input
                      type="number"
                      value={missionForm.salary_gross_monthly || ''}
                      onChange={(e) => setMissionForm(prev => ({
                        ...prev, 
                        salary_gross_monthly: parseFloat(e.target.value) || 0
                      }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Salaire net réel (€/mois)</Label>
                    <Input
                      type="number"
                      value={missionForm.salary_net_monthly || ''}
                      onChange={(e) => setMissionForm(prev => ({
                        ...prev, 
                        salary_net_monthly: parseFloat(e.target.value) || 0
                      }))}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={missionForm.notes || ''}
                    onChange={(e) => setMissionForm(prev => ({...prev, notes: e.target.value}))}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMission(false)}>Annuler</Button>
            <Button onClick={handleSaveMission} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateFinancialsCard;
